import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Box, IconButton } from '@mui/material';
import { CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import { TypographyWithSkeleton } from 'app/shared/componentsRestyled/coreDesignSystem/Typography';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { closeDocumentPaneAction } from 'store/loanApplications/actions';

import {
  paneBodyStyles,
  paneHeaderStyles,
  paneStyles,
  paneTitleStyles,
} from './LoanDocumentPane.styles';
import { LoanDocumentPaneTestIds } from './LoanDocumentPane.testIds';

/**
 * A document side pane: a close header plus a render-slot the host fills with a full Documents
 * browser (tree + selection + viewer). Host-agnostic — the host provides `renderDocuments` so the
 * same pane works for the loan editor, the legacy documents page, and subcompany documents.
 */
const LoanDocumentPane = ({ pane, renderDocuments }) => {
  const dispatch = useDispatch();

  const onClose = useCallback(
    () => dispatch(closeDocumentPaneAction(pane.paneId)),
    [dispatch, pane.paneId],
  );

  return (
    <Box sx={paneStyles}>
      <Box sx={paneHeaderStyles}>
        <TypographyWithSkeleton color="text.primary" sx={paneTitleStyles} variant="medium14">
          Documents
        </TypographyWithSkeleton>

        <IconButton
          aria-label="Close document pane"
          data-testid={LoanDocumentPaneTestIds.CLOSE_BUTTON}
          size="small"
          onClick={onClose}
        >
          <CancelIcon size={IconSize[20]} />
        </IconButton>
      </Box>

      <Box sx={paneBodyStyles}>{renderDocuments()}</Box>
    </Box>
  );
};

LoanDocumentPane.propTypes = {
  pane: PropTypes.shape({ paneId: PropTypes.string }).isRequired,
  renderDocuments: PropTypes.func.isRequired,
};

export default memo(LoanDocumentPane);
